<template>
    <div class="relative inline-block">
        <button
            ref="trigger"
            type="button"
            :class="[
                'cursor-pointer rounded-full text-foxy-text-secondary transition-colors hover:bg-foxy-elevated hover:text-foxy-text focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-foxy-action/20',
                comfortableTrigger
                    ? 'inline-flex min-h-11 min-w-11 items-center justify-center p-0'
                    : 'p-1',
            ]"
            aria-label="Інші дії"
            title="Інші дії"
            aria-haspopup="menu"
            :aria-expanded="showDropdown ? 'true' : 'false'"
            data-report-trigger
            @click.stop="toggleDropdown"
            @keydown.stop
        >
            <i class="fas fa-ellipsis-v text-sm" aria-hidden="true"></i>
        </button>

        <div
            v-if="showDropdown"
            ref="menu"
            v-report-menu-portal
            class="report-action-menu"
            :style="menuStyle"
            role="menu"
            @click.stop
            @keydown="handleMenuKeydown"
        >
            <button
                v-if="shareUrl"
                type="button"
                role="menuitem"
                data-report-menuitem
                data-report-share-action
                @click="sharePublication"
            >
                <i class="fas fa-share-alt" aria-hidden="true"></i>
                Поділитися
            </button>
            <a
                v-if="editUrl"
                :href="editUrl"
                role="menuitem"
                data-report-menuitem
                @click="closeDropdown"
            >
                <i class="fas fa-edit" aria-hidden="true"></i>
                Редагувати
            </a>
            <button
                v-if="deleteUrl"
                type="button"
                role="menuitem"
                data-report-menuitem
                class="text-foxy-error"
                :disabled="deleting"
                @click="deleteContent"
            >
                <i class="fas fa-trash" aria-hidden="true"></i>
                {{ deleting ? 'Видалення…' : 'Видалити' }}
            </button>
            <button
                v-if="reportEnabled && !hasOwnerActions"
                type="button"
                role="menuitem"
                data-report-menuitem
                class="text-foxy-error"
                @click="openReportModal"
            >
                <i class="fas fa-flag" aria-hidden="true"></i>
                {{ reportLabel }}
            </button>
        </div>

        <ReportModal
            v-if="reportEnabled"
            :show="showReportModal"
            :reportable-type="reportableType"
            :reportable-id="reportableId"
            :content-preview="contentPreview"
            :content-url="contentUrl"
            @close="closeReportModal"
        />
    </div>
</template>

<script>
import ReportModal from './ReportModal.vue';
import { shareContent as shareCanonicalContent } from '../utils/shareHelper';

const MENU_PADDING = 12;
const MENU_GAP = 8;

export default {
    name: 'ReportButton',
    components: { ReportModal },
    directives: {
        reportMenuPortal: {
            inserted(element) {
                if (element.parentNode !== document.body) document.body.appendChild(element);
            },
        },
    },
    props: {
        reportableType: { type: String, required: true },
        reportableId: { type: [Number, String], required: true },
        contentPreview: { type: String, default: '' },
        contentUrl: { type: String, default: '' },
        editUrl: { type: String, default: null },
        deleteUrl: { type: String, default: null },
        deletedRedirectUrl: { type: String, default: '' },
        comfortableTrigger: { type: Boolean, default: false },
        reportEnabled: { type: Boolean, default: true },
        reportLabel: { type: String, default: 'Поскаржитися' },
        shareUrl: { type: String, default: '' },
        shareTitle: { type: String, default: '' },
        shareText: { type: String, default: '' },
    },
    data() {
        return {
            showDropdown: false,
            showReportModal: false,
            deleting: false,
            menuStyle: { visibility: 'hidden' },
        };
    },
    computed: {
        hasOwnerActions() {
            return Boolean(this.editUrl || this.deleteUrl);
        },
    },
    methods: {
        toggleDropdown() {
            if (this.showDropdown) return this.closeDropdown({ restoreFocus: true });
            this.showDropdown = true;
            this.$nextTick(() => {
                this.positionDropdown();
                this.$nextTick(() => this.menuItems()[0]?.focus());
            });
        },
        positionDropdown() {
            const trigger = this.$refs.trigger;
            const menu = this.$refs.menu;
            if (!trigger || !menu) return;
            const rect = trigger.getBoundingClientRect();
            const width = menu.offsetWidth || 208;
            const height = menu.offsetHeight || 176;
            let left = rect.right - width;
            left = Math.max(MENU_PADDING, Math.min(left, window.innerWidth - width - MENU_PADDING));
            let top = rect.bottom + MENU_GAP;
            if (top + height > window.innerHeight - MENU_PADDING) top = rect.top - height - MENU_GAP;
            top = Math.max(MENU_PADDING, Math.min(top, window.innerHeight - height - MENU_PADDING));
            this.menuStyle = {
                left: `${Math.round(left)}px`,
                top: `${Math.round(top)}px`,
                visibility: 'visible',
            };
        },
        closeDropdown({ restoreFocus = false } = {}) {
            if (!this.showDropdown) return;
            this.showDropdown = false;
            this.menuStyle = { visibility: 'hidden' };
            if (restoreFocus) this.$nextTick(() => this.$refs.trigger?.focus());
        },
        menuItems() {
            return Array.from(this.$refs.menu?.querySelectorAll('[data-report-menuitem]') || []);
        },
        handleMenuKeydown(event) {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                this.closeDropdown({ restoreFocus: true });
                return;
            }
            const items = this.menuItems();
            if (!items.length) return;
            const current = Math.max(0, items.indexOf(document.activeElement));
            let next = null;
            if (event.key === 'ArrowDown') next = (current + 1) % items.length;
            if (event.key === 'ArrowUp') next = (current - 1 + items.length) % items.length;
            if (event.key === 'Home') next = 0;
            if (event.key === 'End') next = items.length - 1;
            if (next !== null) {
                event.preventDefault();
                items[next].focus();
            }
        },
        openReportModal() {
            if (typeof this.closeDropdown === 'function') this.closeDropdown();
            else this.showDropdown = false;
            this.showReportModal = true;
        },
        closeReportModal() {
            this.showReportModal = false;
            this.$nextTick(() => this.$refs.trigger?.focus());
        },
        async sharePublication() {
            if (typeof this.closeDropdown === 'function') this.closeDropdown();
            else this.showDropdown = false;
            const payload = { title: this.shareTitle, url: this.shareUrl };
            if (this.shareText) payload.text = this.shareText;
            await shareCanonicalContent(payload);
        },
        async deleteContent() {
            const confirmed = await window.confirm(
                'Ви впевнені, що хочете видалити цю публікацію?',
                'Підтвердження',
                'warning',
            );
            if (!confirmed) return;

            this.deleting = true;
            this.closeDropdown();
            try {
                const response = await window.axios.delete(this.deleteUrl);
                if (!response.data.success) {
                    throw new Error(response.data.message || 'Не вдалося видалити публікацію.');
                }
                this.$emit('deleted', this.reportableId);
                if (this.deletedRedirectUrl) {
                    window.location.assign(this.deletedRedirectUrl);
                    return;
                }
                if (typeof window.showNotification === 'function') {
                    window.showNotification(response.data.message || 'Публікацію видалено.', 'success');
                }
            } catch (error) {
                console.error('Error deleting review:', error);
                this.deleting = false;
                const message = error?.response?.data?.message || error.message || 'Не вдалося видалити публікацію.';
                if (typeof window.showNotification === 'function') window.showNotification(message, 'error');
                else window.alert(message);
            }
        },
        handleClickOutside(event) {
            if (this.$el?.contains(event.target) || this.$refs.menu?.contains(event.target)) return;
            this.closeDropdown();
        },
        handleViewportChange() {
            if (this.showDropdown) this.positionDropdown();
        },
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
        window.addEventListener('resize', this.handleViewportChange);
        window.addEventListener('scroll', this.handleViewportChange, true);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
        window.removeEventListener('resize', this.handleViewportChange);
        window.removeEventListener('scroll', this.handleViewportChange, true);
    },
};
</script>

<style scoped>
.report-action-menu {
    position: fixed;
    z-index: 120;
    min-width: 13rem;
    max-width: calc(100vw - 1.5rem);
    overflow: hidden;
    border: 1px solid rgb(var(--foxy-border));
    border-radius: 0.75rem;
    background: rgb(var(--foxy-surface));
    box-shadow: 0 14px 34px rgb(var(--foxy-cocoa) / 0.24);
}
.report-action-menu > button,
.report-action-menu > a {
    display: flex;
    width: 100%;
    min-height: 2.75rem;
    align-items: center;
    gap: 0.65rem;
    padding: 0.7rem 0.85rem;
    color: rgb(var(--foxy-text-secondary));
    font-size: 0.8rem;
    font-weight: 700;
    text-align: left;
}
.report-action-menu > button:hover,
.report-action-menu > button:focus-visible,
.report-action-menu > a:hover,
.report-action-menu > a:focus-visible {
    background: rgb(var(--foxy-elevated));
    color: rgb(var(--foxy-text));
    outline: none;
}
.report-action-menu > .text-foxy-error {
    color: rgb(var(--foxy-error));
}
</style>
